/**
 * The language registry, as the frontend sees it (ADR-111).
 *
 * Each fact has one home, and this module joins them:
 *  - code, English and native names, regional tag, release: the backend
 *    registry (`utils/language_data.py` + `settings.RELEASED_LANGUAGES`),
 *    served by GET /languages/. The frontend keeps no copy.
 *  - interface table and its review status: the files under ./locales/,
 *    discovered by ./app.ts.
 *
 * Every list of interface or native languages derives from here. Do not add a
 * hard-coded list of languages anywhere else.
 */
import { useEffect, useSyncExternalStore } from 'react'
import apiClient from '../api/client'
import { INTERFACE_TABLES, type AppLocale, type InterfaceTableMeta } from './app'

export interface RegistryLanguage {
  code: string
  /** English name, e.g. "Russian". */
  name: string
  /** The language's own name, e.g. "Русский". */
  nativeName: string
  /** Regional tag for the presentation edge only (D1), e.g. "ru-RU". */
  tag: string
  /** The owner has released it (D5). */
  released: boolean
  /** Its interface table, or null when it has none. */
  interfaceTable: InterfaceTableMeta | null
}

interface ApiLanguage {
  code: string
  name: string
  native_name: string
  tag: string
  released: boolean
}

let languages: RegistryLanguage[] | null = null
let request: Promise<RegistryLanguage[]> | null = null
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Fetch the registry once; concurrent callers share the request, and a failed
 *  fetch is tried again by the next caller. */
export function loadLanguageRegistry(): Promise<RegistryLanguage[]> {
  if (languages) return Promise.resolve(languages)
  if (!request) {
    // Started inside a promise so a client that throws synchronously still
    // yields a rejected request rather than an exception in a render effect.
    request = Promise.resolve()
      .then(() => apiClient.get<{ languages: ApiLanguage[] }>('/api/v1/languages/'))
      .then(({ data }) => {
        const joined = data.languages.map((l) => ({
          code: l.code,
          name: l.name,
          nativeName: l.native_name,
          tag: l.tag || l.code,
          released: l.released === true,
          interfaceTable: INTERFACE_TABLES[l.code] ?? null,
        }))
        languages = joined
        rememberReleased(joined)
        listeners.forEach((notify) => notify())
        return joined
      })
      .finally(() => {
        request = null
      })
  }
  return request
}

// The released codes from the last registry this browser read: a per-viewer
// hint for main.tsx's first-paint guess (landing.guessLocale), never a source
// of what is offered. Storage may be unavailable; then there is no hint.
const RELEASED_HINT_KEY = 'cs-released-languages'

function rememberReleased(registry: RegistryLanguage[]): void {
  try {
    localStorage.setItem(RELEASED_HINT_KEY, JSON.stringify(registry.filter((l) => l.released).map((l) => l.code)))
  } catch { /* no hint */ }
}

/** Codes released when this browser last read the registry, or null. */
export function rememberedReleasedCodes(): string[] | null {
  try {
    const raw = localStorage.getItem(RELEASED_HINT_KEY)
    const codes: unknown = raw ? JSON.parse(raw) : null
    return Array.isArray(codes) ? codes.filter((c): c is string => typeof c === 'string') : null
  } catch {
    return null
  }
}

/** The registry if it has loaded, else null. */
export function getLanguageRegistry(): RegistryLanguage[] | null {
  return languages
}

/** The registry if it has loaded, else null, without starting a fetch: for
 *  readers that only refine with it (the regional tag), since main.tsx loads it
 *  before the first paint. */
export function useLoadedLanguageRegistry(): RegistryLanguage[] | null {
  return useSyncExternalStore(subscribe, getLanguageRegistry)
}

/** The registry, or null until it has loaded (or while it cannot be read).
 *  Fetches it if needed: for screens that list languages. */
export function useLanguageRegistry(): RegistryLanguage[] | null {
  const snapshot = useLoadedLanguageRegistry()
  useEffect(() => {
    if (!languages) loadLanguageRegistry().catch(() => { /* callers fall back to English */ })
  }, [])
  return snapshot
}

/**
 * Languages a user may choose as interface language, or as native language at
 * onboarding (D4): an interface table, and released (D5). Registry order.
 * Until the registry can be read, English alone: it is the fallback table and
 * the one language that is always offered.
 */
export function selectableInterfaceLanguages(registry: RegistryLanguage[] | null): RegistryLanguage[] {
  if (!registry) return [ENGLISH_FALLBACK]
  return registry.filter((l) => l.interfaceTable !== null && l.released)
}

/** The regional tag for a code (D1); the bare code until the registry loads,
 *  which Intl and `lang` both accept. */
export function tagFor(code: string, registry: RegistryLanguage[] | null): string {
  return registry?.find((l) => l.code === code)?.tag ?? code
}

/** The browser's preferred languages, most preferred first. */
export function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return []
  // `language` is the first of `languages` in a browser; reading it first keeps
  // a viewer that sets only `language` working.
  return [navigator.language, ...(navigator.languages ?? [])].filter(Boolean)
}

/** The first of the browser's preferred languages (primary subtag) that is
 *  offered, else English. A bare string is a one-language preference. */
export function matchBrowserLanguage(
  browserLanguage: string | readonly string[],
  offered: readonly string[],
): AppLocale {
  const preferred = typeof browserLanguage === 'string' ? [browserLanguage] : browserLanguage
  for (const tag of preferred) {
    const primary = tag.toLowerCase().split('-')[0]
    if (offered.includes(primary)) return primary
  }
  return 'en'
}

const ENGLISH_FALLBACK: RegistryLanguage = {
  code: 'en',
  name: 'English',
  nativeName: 'English',
  tag: 'en',
  released: true,
  interfaceTable: INTERFACE_TABLES.en ?? null,
}

/** Test seam: forget the loaded registry. */
export function resetLanguageRegistryForTests(): void {
  languages = null
  request = null
}
